import {
  AccessRequirement,
  AccessSnapshot,
  checkAccess,
} from "./permissions";
import {
  DEFAULT_ROUTE_ACCESS,
  ROUTE_ACCESS,
} from "./routeAccess.config";

type RouteNode = {
  key?: string;
  type?: string;
  route?: string;
  hidden?: boolean;
  collapse?: RouteNode[];
  [key: string]: unknown;
};

function getRouteRequirement(key?: string): AccessRequirement {
  if (!key) return DEFAULT_ROUTE_ACCESS;
  return ROUTE_ACCESS[key] ?? DEFAULT_ROUTE_ACCESS;
}

function isRouteVisible(route: RouteNode, access: AccessSnapshot): boolean {
  if (route.hidden) return false;
  if (!route.key) return true;
  return checkAccess(access, getRouteRequirement(route.key));
}

function filterCollapseItems(
  items: RouteNode[],
  access: AccessSnapshot
): RouteNode[] {
  return items.filter((item) => {
    if (item.collapse?.length) {
      const nested = filterCollapseItems(item.collapse, access);
      return nested.length > 0;
    }
    return isRouteVisible(item, access);
  }).map((item) => {
    if (!item.collapse?.length) return item;
    return {
      ...item,
      collapse: filterCollapseItems(item.collapse, access),
    };
  });
}

export function filterRoutesByAccess(
  routes: RouteNode[],
  access: AccessSnapshot
): RouteNode[] {
  if (access.isSuperAdmin) return routes;

  const result: RouteNode[] = [];

  for (const route of routes) {
    if (route.type === "title") {
      continue;
    }

    if (route.type === "divider") {
      result.push(route);
      continue;
    }

    if (route.collapse?.length) {
      // Menu pai com key (ex.: Financeiro) respeita o mesmo controle de acesso
      if (route.key && !isRouteVisible(route, access)) continue;

      const collapse = filterCollapseItems(route.collapse, access);
      if (!collapse.length) continue;

      // Menu pai só aparece se houver ao menos um filho visível
      result.push({ ...route, collapse });
      continue;
    }

    if (route.route && !isRouteVisible(route, access)) continue;
    if (!route.route && route.collapse) continue;

    result.push(route);
  }

  return result.filter((route, index, list) => {
    if (route.type !== "divider") return true;
    const prev = list[index - 1];
    const next = list[index + 1];
    return (
      prev &&
      next &&
      prev.type !== "divider" &&
      next.type !== "divider"
    );
  });
}

export function buildRouteAccessMap(
  routes: RouteNode[]
): Map<string, AccessRequirement> {
  const map = new Map<string, AccessRequirement>();

  const walk = (nodes: RouteNode[]) => {
    for (const node of nodes) {
      if (node.route && node.key) {
        const path = node.route.startsWith("/")
          ? node.route
          : `/${node.route}`;
        map.set(path, getRouteRequirement(node.key));
      }
      if (node.collapse?.length) walk(node.collapse);
    }
  };

  walk(routes);
  return map;
}

export function resolveAccessForPath(
  pathname: string,
  routeMap: Map<string, AccessRequirement>,
  extraRoutes: Record<string, AccessRequirement>
): AccessRequirement | null {
  if (routeMap.has(pathname)) {
    return routeMap.get(pathname)!;
  }

  for (const [pattern, requirement] of Object.entries(extraRoutes)) {
    if (pathname === pattern || pathname.startsWith(`${pattern}/`)) {
      return requirement;
    }
  }

  for (const [path, requirement] of routeMap.entries()) {
    if (pathname.startsWith(`${path}/`)) {
      return requirement;
    }
  }

  return null;
}

export function canAccessPath(
  pathname: string,
  access: AccessSnapshot,
  routes: RouteNode[],
  extraRoutes: Record<string, AccessRequirement>
): boolean {
  if (access.isSuperAdmin) return true;
  const map = buildRouteAccessMap(routes);
  const requirement =
    resolveAccessForPath(pathname, map, extraRoutes) ?? DEFAULT_ROUTE_ACCESS;
  return checkAccess(access, requirement);
}
